import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ReferenceField } from 'react-admin';
import {
    getAntennaPoi,
    type AntennaId,
    type AntennaPoiMember,
    type AntennaPoiMission,
    type MemberId,
    type MissionId,
} from '@wwf/sdk';
import { MissionLightCard } from '@app/mission/show/MissionLightCard';
import { MemberLightCard } from '@app/member/show/MemberLightCard';
import type { Theme } from '@mui/material/styles';

const INITIAL_CENTER: [number, number] = [46.352, 2.6]; // center of France
const INITIAL_ZOOM = 6;

interface MemberPoint {
    coordinates: [number, number]; // [lng, lat] GeoJSON order
    members: AntennaPoiMember[];
}

interface MissionPoint {
    id: MissionId;
    position: [number, number]; // [lat, lng] for Leaflet
    mission: AntennaPoiMission;
}

function parseCoord(value: string | number | null | undefined): number | null {
    if (value == null) return null;
    const n = typeof value === 'string' ? parseFloat(value) : value;
    return Number.isFinite(n) ? n : null;
}

function getPoiMemberCoordinates(
    member: AntennaPoiMember
): [number, number] | null {
    const lat = parseCoord(member.coordinates.latitude);
    const lng = parseCoord(member.coordinates.longitude);
    if (lat == null || lng == null) return null;
    return [lng, lat];
}

function toMemberPointData(members: AntennaPoiMember[]): MemberPoint[] {
    const coordinatesMap = members.reduce((acc, member) => {
        const position = getPoiMemberCoordinates(member);
        if (!position) return acc;
        const key = position.join(',');

        const existing = acc.get(key);
        if (existing) {
            acc.set(key, {
                coordinates: position,
                members: [...existing.members, member],
            });
        } else {
            acc.set(key, { coordinates: position, members: [member] });
        }
        return acc;
    }, new Map<string, MemberPoint>());

    return Array.from(coordinatesMap.values());
}

function toMissionPoints(missions: AntennaPoiMission[]): MissionPoint[] {
    const result: (MissionPoint | null)[] = missions.map(mission => {
        const lat = parseCoord(mission.coordinates.latitude);
        const lng = parseCoord(mission.coordinates.longitude);
        if (lat == null || lng == null || !mission.id) return null;
        return {
            id: mission.id,
            position: [lat, lng] as [number, number],
            mission,
        };
    });
    return result.filter((p): p is MissionPoint => p != null);
}

// coordinates in [lng, lat] order
function getBounds(coordinates: [number, number][]): L.LatLngBounds | null {
    if (coordinates.length === 0) return null;
    let minLng = coordinates[0][0];
    let maxLng = coordinates[0][0];
    let minLat = coordinates[0][1];
    let maxLat = coordinates[0][1];

    for (let i = 1; i < coordinates.length; i++) {
        const [lng, lat] = coordinates[i];
        minLng = Math.min(minLng, lng);
        maxLng = Math.max(maxLng, lng);
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
    }
    return L.latLngBounds([minLat, minLng], [maxLat, maxLng]);
}

function allCoordinatesForBounds(
    memberPointList: MemberPoint[],
    missionPoints: MissionPoint[]
): [number, number][] {
    const memberCoords = memberPointList.map(p => p.coordinates);
    const missionCoords = missionPoints.map(
        m => [m.position[1], m.position[0]] as [number, number]
    );
    return [...memberCoords, ...missionCoords];
}

function createMemberMarkerIcon(theme: Theme, count?: number): L.DivIcon {
    const bg = theme.palette.secondary.main;
    const color = theme.palette.secondary.contrastText;
    const border = theme.palette.background.paper;
    const label = count != null ? String(count) : '1';
    return L.divIcon({
        className: 'poi-marker-count poi-marker-member',
        html: `<span style="display:flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:50%;background:${bg};color:${color};font-size:12px;font-weight:600;border:2px solid ${border};box-shadow:0 1px 3px rgba(0,0,0,0.3);">${label}</span>`,
        iconSize: [28, 28],
        iconAnchor: [14, 14],
    });
}

function createMissionMarkerIcon(theme: Theme): L.DivIcon {
    const bg = theme.palette.primary.main;
    const color = theme.palette.primary.contrastText;
    const border = theme.palette.background.paper;
    return L.divIcon({
        className: 'poi-marker-mission',
        html: `<span style="display:flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:50%;background:${bg};color:${color};font-size:12px;font-weight:600;border:2px solid ${border};box-shadow:0 1px 3px rgba(0,0,0,0.3);">M</span>`,
        iconSize: [28, 28],
        iconAnchor: [14, 14],
        popupAnchor: [0, -14],
    });
}

function MapFitBounds({
    memberPointList,
    missionPoints,
}: {
    memberPointList: MemberPoint[];
    missionPoints: MissionPoint[];
}) {
    const map = useMap();
    const fitted = useRef(false);

    useEffect(() => {
        const coords = allCoordinatesForBounds(memberPointList, missionPoints);
        if (coords.length === 0 || fitted.current) return;
        const bounds = getBounds(coords);
        if (bounds) {
            map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
            fitted.current = true;
        }
    }, [map, memberPointList, missionPoints]);

    return null;
}

export interface AntennaPOIMapProps {
    antennaId: AntennaId;
}

export const AntennaPOIMap = ({ antennaId }: AntennaPOIMapProps) => {
    const theme = useTheme();
    const [memberPointList, setMemberPointList] = useState<MemberPoint[]>([]);
    const [missionPoints, setMissionPoints] = useState<MissionPoint[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const defaultMemberIcon = useMemo(
        () => createMemberMarkerIcon(theme),
        [theme]
    );
    const missionIcon = useMemo(() => createMissionMarkerIcon(theme), [theme]);
    const popupBackground =
        (theme.palette as { map?: { popup?: { background?: string } } }).map
            ?.popup?.background ?? theme.palette.background.paper;

    useEffect(() => {
        let cancelled = false;

        async function fetchPOI() {
            setLoading(true);
            setError(null);
            try {
                const antennaPoi = await getAntennaPoi({
                    filter: {
                        memberAntennaId: antennaId,
                        missionAntennaId: antennaId,
                    },
                });

                if (cancelled) return;

                const members = antennaPoi.members ?? [];
                const missions = antennaPoi.missions ?? [];
                setMemberPointList(toMemberPointData(members));
                setMissionPoints(toMissionPoints(missions));
            } catch (e) {
                if (!cancelled) {
                    setError(e instanceof Error ? e.message : 'Failed to load');
                    setMemberPointList([]);
                    setMissionPoints([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        void fetchPOI();
        return () => {
            cancelled = true;
        };
    }, [antennaId]);

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={320}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box py={2}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    const hasAnyPoints = memberPointList.length > 0 || missionPoints.length > 0;

    if (!hasAnyPoints) {
        return (
            <Box py={2}>
                <Typography color="text.secondary">
                    No members or missions with localisation for this antenna.
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <style>{`
                .poi-marker-count.leaflet-marker-icon, .poi-marker-mission.leaflet-marker-icon { background: transparent !important; border: none !important; }
                .leaflet-popup.poi-popup-theme .leaflet-popup-content-wrapper {
                    background: ${popupBackground};
                    color: ${theme.palette.text.primary};
                    box-shadow: ${theme.shadows[8]};
                }
                .leaflet-popup.poi-popup-theme .leaflet-popup-tip {
                    background: ${popupBackground};
                    border-top-color: ${popupBackground};
                }
            `}</style>
            <Box
                sx={{
                    height: '60vh',
                    width: '100%',
                    borderRadius: 2,
                    overflow: 'hidden',
                }}
            >
                <MapContainer
                    center={INITIAL_CENTER}
                    zoom={INITIAL_ZOOM}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom
                >
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <MapFitBounds
                        memberPointList={memberPointList}
                        missionPoints={missionPoints}
                    />
                    {memberPointList.map((point, index) => (
                        <Marker
                            key={`member-${point.coordinates.join(',')}-${index}`}
                            position={[
                                point.coordinates[1],
                                point.coordinates[0],
                            ]}
                            icon={
                                point.members.length > 1
                                    ? createMemberMarkerIcon(
                                          theme,
                                          point.members.length
                                      )
                                    : defaultMemberIcon
                            }
                        >
                            <Popup
                                className="poi-popup-theme"
                                minWidth={100}
                                maxWidth={360}
                            >
                                <Box
                                    sx={{
                                        maxHeight: 320,
                                        overflow: 'auto',
                                    }}
                                >
                                    {point.members.map(member => (
                                        <ReferenceField
                                            key={member.id}
                                            reference="member"
                                            source="id"
                                            record={{
                                                id: member.id as MemberId,
                                            }}
                                            link={false}
                                        >
                                            <MemberLightCard />
                                        </ReferenceField>
                                    ))}
                                </Box>
                            </Popup>
                        </Marker>
                    ))}
                    {missionPoints.map(missionPoint => (
                        <Marker
                            key={`mission-${missionPoint.id}`}
                            position={missionPoint.position}
                            icon={missionIcon}
                        >
                            <Popup
                                className="poi-popup-theme"
                                minWidth={100}
                                maxWidth={360}
                            >
                                <ReferenceField
                                    reference="mission"
                                    source="id"
                                    record={{ id: missionPoint.mission.id }}
                                    link={false}
                                >
                                    <MissionLightCard />
                                </ReferenceField>
                            </Popup>
                        </Marker>
                    ))}
                </MapContainer>
            </Box>
        </Box>
    );
};
